import React, {useEffect} from 'react';
import PropTypes from 'prop-types';
import ContentCard from '~/components/ContentCard';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  Typography,
  CardSelectButton,
  Stack
} from '@customink/pigment-react';
import {useNavigate} from 'react-router-dom';
import {connect, useDispatch, useSelector} from 'react-redux';
import {
  setOrderType,
  getOrderType,
  setOrderOptionsPath
} from '~/slices/pathSlice';
import {metrics} from '~/vendor/metrics';
import {bindActionCreators} from 'redux';
import {ActionCreators} from 'redux-undo';
import Scroller from '~/utils/components/ScrollingContainer';
import {createStructuredSelector} from 'reselect';
import withRouter from '../../router/withRouter';
import {pushGtmEvent} from '~/utils/analytics';

import {simplePricingTestEnabled} from '~/selectors/ConfigSelectors';

import './index.scss';
import MediaQueries, {breakpoint} from '../../constants/MediaQueries';

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
const OrderType = ({onClose, setOrderOptionsPath}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const selection = useSelector(getOrderType);
  const setSelection = orderType => dispatch(setOrderType(orderType));
  const simplePricingEnabled = useSelector(simplePricingTestEnabled);

  useEffect(() => {
    pushGtmEvent({
      event: 'lab_quote_wto',
      wto_scenario:
        selection === 'order-options' ? 'wto_buy_and_ship' : 'wto_fundraiser'
    });
  }, [selection]);

  const onNext = () => {
    let eventLabel = '';
    if (selection === 'order-options') {
      eventLabel = 'buy and ship';
    } else if (selection === 'fundraising') {
      eventLabel = 'fundraising';
    }
    metrics.fireEvent('lab ways to order', 'ship or sell modal', eventLabel);
    setOrderOptionsPath('/next/quote');
    navigate(`/next/${selection}`);
  };

  const onBack = () => {
    pushGtmEvent({
      event: 'lab_interaction',
      lab_interaction_location: 'lab_quote_wto',
      lab_interaction_type: 'back arrow'
    });
    if (simplePricingEnabled) {
      navigate('/next/price');
    } else {
      onClose();
    }
  };

  const isMobile = breakpoint(MediaQueries.onlySmallAndXSmall);

  return (
    <ContentCard
      contentClassName="order-options"
      onNavBack={onBack}
      onNavClose={onClose}>
      <Scroller>
        <div className="order-options-content">
          <Typography variant="heading3">
            What do you want to do with your design?
          </Typography>
          <Card
            selected={selection === 'order-options'}
            onClick={() => setSelection('order-options')}
            sx={{
              cursor: 'pointer'
            }}>
            <CardSelectButton
              selected={selection === 'order-options'}
              sx={{
                display: selection === 'order-options' ? undefined : 'none'
              }}
            />
            <CardHeader
              sx={{
                mr: 8
              }}
              title="Buy & Ship"
              className="order-options-card-header"
            />
            <CardContent>
              Enter your order details and ship to one or multiple addresses
              right away. You can also collect sizes, addresses, and payments
              from your group.
            </CardContent>
          </Card>
          <Card
            selected={selection === 'fundraising'}
            onClick={() => setSelection('fundraising')}
            sx={{
              cursor: 'pointer'
            }}>
            <CardSelectButton
              selected={selection === 'fundraising'}
              sx={{
                display: selection === 'fundraising' ? undefined : 'none'
              }}
            />
            <CardHeader
              sx={{
                mr: 8
              }}
              title="Start a Fundraiser"
              className="order-options-card-header"
            />
            <CardContent>
              Sell your designs online to raise money and spread awareness for
              your cause. You can build your own campaign page and even accept
              donations.
            </CardContent>
          </Card>
        </div>
      </Scroller>
      <Stack
        sx={{borderTop: '1px solid #e3e3e3', px: 2, py: {sm: 1.5, md: 2}}}
        justifyContent="flex-end"
        className="order-options-actions"
        data-testid="order-options-actions"
        direction="row">
        <Button
          size="large"
          onClick={onNext}
          disabled={!selection}
          fullWidth={isMobile}
          sx={{minWidth: 160}}
          data-testid="order-type-continue">
          Continue
        </Button>
      </Stack>
    </ContentCard>
  );
};

const propTypes = {
  setOrderOptionsPath: PropTypes.func,
  onClose: PropTypes.func
};

OrderType.propTypes = propTypes;

const select = () => {
  return createStructuredSelector({});
};

const actions = dispatch => ({
  ...bindActionCreators(
    {
      setOrderOptionsPath: setOrderOptionsPath
    },
    dispatch
  ),
  ...bindActionCreators(ActionCreators, dispatch)
});

export default connect(select, actions)(withRouter(OrderType));
